import type { JSX } from 'react';

import { Spinner } from '@change/ds-core-ui/Spinner';

import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import { withPrefetchedData } from '#src/shared/prefetch';

import type { PsfPromoteOrSharePagePrefetchedDataProps } from '#src/app/pages/psf/promoteOrShare/pageContext';
import {
	prefetchPageContext,
	PsfPromoteOrSharePageContextProvider,
} from '#src/app/pages/psf/promoteOrShare/pageContext';
import { isLoaded } from '#src/app/shared/utils/async';

import { Layout } from '../layout';

import { PsfPromoteOrShareErrorHandler } from './ErrorHandler';

/** @knip-ignore production only calls this from `PsfPromoteOrShare` in this same file; `tests-helpers/setup.tsx` renders it directly to drive integration tests */
export const PsfPromoteOrShareEntryPoint = ({
	prefetchedData,
}: PsfPromoteOrSharePagePrefetchedDataProps): JSX.Element => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();

	return (
		<PsfPromoteOrSharePageContextProvider
			slug={slug}
			prefetchedData={prefetchedData}
			errorHandler={PsfPromoteOrShareErrorHandler}
			loadingFallback={<Spinner className="mx-auto my-64" />}
		>
			<Layout />
		</PsfPromoteOrSharePageContextProvider>
	);
};

export const PsfPromoteOrShare = withPrefetchedData(
	function PsfPromoteOrShare({ prefetchedData }: PsfPromoteOrSharePagePrefetchedDataProps) {
		return <PsfPromoteOrShareEntryPoint prefetchedData={prefetchedData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context),
		pageTitle: ({ i18n, prefetchedData, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { ask } = prefetchedData.pageData.pageData.petition;
			return fromTemplate(i18n.translate('fe.pages.sponsors.title', { petition_title: ask }));
		},
	},
);
